// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: a nudge from Konvo, as the person sees it in the Konvo DM (design/assistant-engagement
// sheet 14): under Konvo's message, a card with Connect (while not connected) and up to three
// things to try from their own week, locked until they connect. Connect opens the Konvo panel's
// connect step; a locked try does the same and keeps its request, so it runs once connected;
// after connecting the rows unlock and each runs in the panel. Small print says only they see it
// and where to switch these messages off. The card JSON rides in the embed (Konvo:nudge).

import {Endpoints} from '@app/features/app/constants/Endpoints';
import Activity from '@app/features/konvo_activity/state/Activity';
import KonvoCalendar from '@app/features/konvo_calendar/state/KonvoCalendar';
import KonvoConnections from '@app/features/konvo_connections/state/KonvoConnections';
import styles from '@app/features/konvo_engagement/components/NudgeCard.module.css';
import {StarterIcon} from '@app/features/konvo_engagement/components/StarterVisuals';
import {openNudgePanel} from '@app/features/konvo_engagement/state/NudgeActions';
import NudgeBookings from '@app/features/konvo_engagement/state/NudgeBookings';
import {upcomingSlots} from '@app/features/konvo_engagement/utils/NudgeHandoff';
import KonvoPanel from '@app/features/konvo_panel/state/KonvoPanel';
import {http} from '@app/features/platform/transport/RestTransport';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {ClaudeLogo} from '@app/features/ui/components/icons/ClaudeLogo';
import {ArrowRightIcon, CheckIcon, LockSimpleIcon} from '@app/features/ui/icons/Icons';
import {Tooltip} from '@app/features/ui/tooltip/Tooltip';
import type {NudgeCard as NudgeCardData} from '@konvo/schema/src/domains/konvo/AssistantEngagement';
import type {MessageDescriptor} from '@lingui/core';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import {useEffect, useRef, useState} from 'react';

const CONNECT_TITLE_DESCRIPTOR = msg({
	message: 'Connect your assistant',
	comment: 'Nudge card in the Konvo DM: heading of the connect row.',
});
const CONNECT_DETAIL_DESCRIPTOR = msg({
	message: 'About a minute · uses your Nuvio Claude plan',
	comment: 'Nudge card in the Konvo DM: under "Connect your assistant".',
});
const CONNECT_DESCRIPTOR = msg({
	message: 'Connect',
	comment: 'Nudge card in the Konvo DM: button that starts connecting the assistant.',
});
const CONNECTED_TITLE_DESCRIPTOR = msg({
	message: 'Connected',
	comment: 'Nudge card in the Konvo DM once the assistant is connected: heading of the connect row.',
});
const CONNECTED_DETAIL_DESCRIPTOR = msg({
	message: 'Your assistant is ready. Try one of these.',
	comment: 'Nudge card in the Konvo DM once the assistant is connected: under "Connected".',
});
const THEN_TRY_DESCRIPTOR = msg({
	message: 'Then try',
	comment: 'Nudge card in the Konvo DM: label above the things to try, which unlock after connecting.',
});
const TRY_DESCRIPTOR = msg({
	message: 'Things to try',
	comment: 'Nudge card in the Konvo DM: label above the things to try when the assistant is connected.',
});
const LOCKED_DESCRIPTOR = msg({
	message: 'Connect your assistant first. This runs as soon as you connect.',
	comment: 'Nudge card in the Konvo DM: tooltip and accessible label of a locked thing to try.',
});
const SMALL_PRINT_DESCRIPTOR = msg({
	message: 'Only you can see this · you can switch these messages off in Settings → Assistant',
	comment: 'Under the nudge card in the Konvo DM.',
});

/** Nudges already reported as read this session. */
const reported = new Set<string>();

const CALL_LABEL_DESCRIPTOR = msg({
	message: 'Want a hand setting up?',
	comment: 'Nudge card: label above the optional 10-minute setup call slots.',
});
const CALL_WITH_DESCRIPTOR = msg({
	message: 'Pick 10 minutes with {name}',
	comment:
		'Nudge card: line above the setup call time slots. {name} is the first name of the person offering the call.',
});
const CALL_SUBJECT_DESCRIPTOR = msg({
	message: 'Konvo assistant setup with {name}',
	comment: 'Subject of the 10-minute calendar event booked from a nudge card. {name} is the person offering the call.',
});
const CALL_BOOKED_DESCRIPTOR = msg({
	message: 'Booked for {time}. It is in your calendar.',
	comment: 'Nudge card after booking the setup call. {time} is the slot, like "Mon 09:30".',
});
const CALL_FAILED_DESCRIPTOR = msg({
	message: 'Could not book that time. Try another one.',
	comment: 'Toast when booking the setup call from a nudge card fails.',
});

function slotLabel(iso: string, locale: string): string {
	const at = new Date(iso);
	const day = at.toLocaleDateString(locale, {weekday: 'short', timeZone: KonvoCalendar.timezone});
	const time = at.toLocaleTimeString(locale, {
		hour: '2-digit',
		minute: '2-digit',
		hour12: false,
		timeZone: KonvoCalendar.timezone,
	});
	return `${day} ${time}`;
}

/** The optional 10-minute setup call: a few free slots; a tap books it in the person's calendar. */
const NudgeCall = observer(function NudgeCall({card}: {card: NudgeCardData}) {
	const {i18n} = useLingui();
	const nudgeId = card.nudge_id;
	const booked = NudgeBookings.bookedStart(nudgeId);
	const busy = NudgeBookings.isPending(nudgeId);
	// Slots drop off once they start; checked again every minute while the card is on screen.
	const [now, setNow] = useState(() => Date.now());
	useEffect(() => {
		if (booked) return;
		const timer = window.setInterval(() => setNow(Date.now()), 60_000);
		return () => window.clearInterval(timer);
	}, [booked]);
	const slots = upcomingSlots(card.call_slots ?? [], now);
	const who = card.call_with;
	if (!who || (!booked && slots.length === 0)) return null;
	const first = who.name.split(' ')[0] || who.name;
	const book = async (slot: {start: string; end: string}) => {
		// One booking per nudge: not while one is on its way, and never again once booked.
		if (Date.parse(slot.start) <= Date.now() || !NudgeBookings.begin(nudgeId)) return;
		let bookedStart: string | null = null;
		try {
			await http.post(Endpoints.KONVO_CALENDAR_EVENTS, {
				body: {
					subject: i18n._(CALL_SUBJECT_DESCRIPTOR, {name: first}),
					start: slot.start,
					end: slot.end,
					is_all_day: false,
					timezone: KonvoCalendar.timezone,
					location: '',
					body_text: '',
					attendees: [who.address],
					optional_attendees: [],
					teams: true,
					konvo_public: false,
					konvo_channel_id: null,
					konvo_guild_id: null,
					konvo_channel_name: null,
					recurrence: null,
				},
			});
			bookedStart = slot.start;
			Activity.track('action.done', {area: 'calendar', action: 'event.create', origin: 'konvo'});
		} catch {
			ToastCommands.createToast({type: 'error', children: i18n._(CALL_FAILED_DESCRIPTOR)});
		} finally {
			NudgeBookings.finish(nudgeId, bookedStart);
		}
	};
	return (
		<div className={styles.call} data-flx="konvo-engagement.nudge.call">
			<div className={styles.label}>{i18n._(CALL_LABEL_DESCRIPTOR)}</div>
			{booked ? (
				<div className={styles.callBooked}>
					<CheckIcon size={15} weight="bold" />
					{i18n._(CALL_BOOKED_DESCRIPTOR, {time: slotLabel(booked, i18n.locale)})}
				</div>
			) : (
				<>
					<div className={styles.callWith}>{i18n._(CALL_WITH_DESCRIPTOR, {name: first})}</div>
					<div className={styles.slots}>
						{slots.map((slot) => (
							<button
								key={slot.start}
								type="button"
								className={styles.slot}
								disabled={busy}
								onClick={() => void book(slot)}
								data-flx="konvo-engagement.nudge.call-slot"
							>
								{slotLabel(slot.start, i18n.locale)}
							</button>
						))}
					</div>
				</>
			)}
		</div>
	);
});

/** The connect row: Claude and Connect while not connected, a tick once the assistant is ready. */
export function ConnectRow({
	connected,
	connectedDetail,
	onConnect,
	flx,
}: {
	connected: boolean;
	/** Under "Connected": what to do now. */
	connectedDetail: MessageDescriptor;
	onConnect: () => void;
	flx: string;
}) {
	const {i18n} = useLingui();
	return (
		<div className={styles.connect}>
			<span className={clsx(styles.logoTile, connected && styles.logoTileDone)} aria-hidden="true">
				{connected ? <CheckIcon size={20} weight="bold" /> : <ClaudeLogo size={24} />}
			</span>
			<div className={styles.connectText}>
				<div className={styles.connectTitle}>
					{i18n._(connected ? CONNECTED_TITLE_DESCRIPTOR : CONNECT_TITLE_DESCRIPTOR)}
				</div>
				<div className={styles.connectDetail}>{i18n._(connected ? connectedDetail : CONNECT_DETAIL_DESCRIPTOR)}</div>
			</div>
			{!connected && (
				<button type="button" className={styles.connectButton} onClick={onConnect} data-flx={flx}>
					{i18n._(CONNECT_DESCRIPTOR)}
				</button>
			)}
		</div>
	);
}

export const NudgeCard = observer(function NudgeCard({
	card,
	channelId = null,
}: {
	card: NudgeCardData;
	/** The Konvo DM the card is in (the Rail hands it to the main window). */
	channelId?: string | null;
}) {
	const {i18n} = useLingui();
	const root = useRef<HTMLDivElement>(null);
	// Linked and switched on (a linked but switched-off assistant cannot run the tries).
	const connected = KonvoPanel.assistantConnected === true;

	useEffect(() => {
		if (KonvoConnections.claude == null) void KonvoConnections.refreshAssistant().catch(() => undefined);
	}, []);

	useEffect(() => {
		const node = root.current;
		if (!node || reported.has(card.nudge_id)) return;
		const observer = new IntersectionObserver((entries) => {
			if (entries.some((entry) => entry.isIntersecting) && !reported.has(card.nudge_id)) {
				reported.add(card.nudge_id);
				Activity.track('nudge.read', {nudge_id: card.nudge_id});
				observer.disconnect();
			}
		});
		observer.observe(node);
		return () => observer.disconnect();
	}, [card.nudge_id]);

	const connect = (tryIndex?: number) => {
		Activity.track('nudge.connect_clicked', {nudge_id: card.nudge_id});
		const entry = tryIndex != null ? card.tries[tryIndex] : undefined;
		openNudgePanel(
			entry ? {starter: entry.starter, request: entry.request, context: entry.context} : null,
			channelId,
			card.nudge_id,
		);
	};
	const run = (index: number) => {
		const entry = card.tries[index];
		if (!entry) return;
		Activity.track('nudge.try_clicked', {nudge_id: card.nudge_id, starter: entry.starter});
		openNudgePanel({starter: entry.starter, request: entry.request, context: entry.context}, channelId, card.nudge_id);
	};
	const locked = !connected;
	const showConnectRow = card.connect || connected;

	return (
		<div ref={root} className={styles.wrap} data-flx="konvo-engagement.nudge">
			<article className={styles.card}>
				{showConnectRow && (
					<ConnectRow
						connected={connected}
						connectedDetail={CONNECTED_DETAIL_DESCRIPTOR}
						onConnect={() => connect()}
						flx="konvo-engagement.nudge.connect"
					/>
				)}
				{card.tries.length > 0 && (
					<div className={clsx(styles.tries, showConnectRow && styles.triesAfterConnect)}>
						<div className={styles.label}>{i18n._(locked ? THEN_TRY_DESCRIPTOR : TRY_DESCRIPTOR)}</div>
						{card.tries.map((entry, index) => {
							const row = (
								<button
									key={`${entry.starter}:${index}`}
									type="button"
									className={clsx(styles.try, locked && styles.tryLocked)}
									onClick={() => (locked ? connect(index) : run(index))}
									aria-label={locked ? `${entry.title}. ${i18n._(LOCKED_DESCRIPTOR)}` : entry.title}
									data-locked={locked || undefined}
									data-flx="konvo-engagement.nudge.try"
								>
									<span className={styles.tryIcon}>
										<StarterIcon starter={entry.starter} size={16} />
									</span>
									<span className={styles.tryText}>
										<span className={styles.tryTitle}>{entry.title}</span>
										<span className={styles.tryReason}>{entry.reason}</span>
									</span>
									{locked ? (
										<LockSimpleIcon size={14} className={styles.tryEnd} />
									) : (
										<ArrowRightIcon size={15} className={styles.tryEnd} />
									)}
								</button>
							);
							return locked ? (
								<Tooltip key={`${entry.starter}:${index}`} text={i18n._(LOCKED_DESCRIPTOR)} position="top">
									{row}
								</Tooltip>
							) : (
								row
							);
						})}
					</div>
				)}
				<NudgeCall card={card} />
			</article>
			<div className={styles.smallPrint}>{i18n._(SMALL_PRINT_DESCRIPTOR)}</div>
		</div>
	);
});
